<script setup lang="ts">
import { computed, defineEmits, inject, nextTick, ref, watch } from 'vue'
import type { PlatformLanguage } from '@/components/interfaces/graphql-common.interfaces.ts'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { ProjectSettingFormKey } from '@/components/projects/settings.types.ts'
import { ProjectSettingsCategory, WidgetLocale } from '@/enums.ts'
import type { IWidgetLocalization, ProjectSettings } from '@/components/projects/interfaces.ts'
import { isObject, isUndefined } from 'lodash'
import { useTranslations} from '@/composables/useTranslations.ts'
import { Field, FieldArray } from 'vee-validate'

type Tab = PlatformLanguage & { isDefault: boolean; isCreated: boolean }
type WidgetLocalizationMap = Record<string, IWidgetLocalization>

const MaxLanguages = 5
const props = defineProps<{
  languages: PlatformLanguage[]
  modelValue: string
}>()
const { t } = useTranslations()
const emit = defineEmits(['update:modelValue'])
const { defineField, setFieldValue } = inject(ProjectSettingFormKey)
const [ widgetLocalization ] = defineField(ProjectSettingsCategory.WidgetLocalization)
const selected = ref<string>(props.modelValue)// TODO: Default locale
const widgetLocalizationMap = computed<WidgetLocalizationMap>(() => {
  return ((widgetLocalization?.value ?? []) as IWidgetLocalization[]).reduce<WidgetLocalizationMap>((acc: WidgetLocalizationMap, l) => {
    acc[l.locale] = l
    return acc
  }, {})
})
const tabs = computed<Tab[]>(() => {
  return props.languages.reduce<Tab[]>((acc, language) => {
    return acc.concat({
      isDefault: widgetLocalizationMap.value[language.locale]?.default ?? false,
      isCreated: isObject(widgetLocalizationMap.value[language.locale]),
      ...language
    })
  }, [])
})

function selectLanguage(locale: string) {
  if (isObject(widgetLocalizationMap.value[locale])) {
    emit('update:modelValue', locale)
    selected.value = locale
  }
}

function makeLanguageDefault (locale: string) {
  // @ts-expect-error Jevgeni fix it later
  if (isUndefined(widgetLocalization.value.find(( l ) => l.locale === locale))) {
    console.log('skip it')
    return
  }

  console.log('makeDefault')

  setFieldValue(
    ProjectSettingsCategory.WidgetLocalization,
    widgetLocalization.value.map((l: IWidgetLocalization) => {
      l.default = l.locale === locale
      return l
    })
  )
}

function deleteLocalization (locale: string): void {
  if (!(widgetLocalization.value.find((l: IWidgetLocalization) => l.locale === locale)?.default ?? true)) {
    setFieldValue(
      ProjectSettingsCategory.WidgetLocalization,
      widgetLocalization.value.filter((l: IWidgetLocalization) => l.locale !== locale)
    )
  }
}

function addLocalization (locale: string): void {
  setFieldValue(ProjectSettingsCategory.WidgetLocalization, widgetLocalization.value.concat({
    default: false,
    locale: locale,
    assistantName: 'Name',
    assistantBadge: '',
    initialMessage: '',
    userMessagePlaceholder: '',
  }))
}
</script>

<template>
  <!--<div class="flex flex-row items-center justify-between">
    <p class="text-sm md:text-base">{{ t('settings.visual.ifYourWebsiteHasMultipleLanguages') }}</p>
    <div class="flex items-center" v-if="languages.length > MaxLanguages">
      <select v-model="selected" class="p-2 px-6 w-32 border font-semibold rounded-2xl text-sm">
        <option v-for="lang in languages" :key="lang.id" :value="lang.locale">
          {{ lang.locale }}
        </option>
      </select>
    </div>
  </div>-->
  <div>
    <ul class="flex flex-wrap -mb-3">
      <li
        v-for="(lang, index) in tabs"
        :key="lang.id"
        class="text-sm font-medium text-center text-gray-500 border-b border-gray-200"
      >
        <div
          class="lang-tab noselect"
          :class="{
            'border-purple-600 text-purple-600 active': lang.locale === selected,
          }"
          @click="selectLanguage(lang.locale)"
          style="cursor: pointer"
        >
          <div class="relative flex gap-3 pb-4 pt-3.5">
            <div class="min-w-0 flex-1 text-sm/6">
              <label for="comments" :class="{
                'font-medium text-gray-900': true,
                'opacity-50': !lang.isCreated
              }">{{ lang.title }}</label>
            </div>
            <div class="flex h-6 shrink-0 items-center">
              <div class="group grid size-4 grid-cols-1" v-if="lang.isCreated">
                <input
                  :disabled="!lang.isCreated || lang.isDefault"
                  :checked="lang.isDefault"
                  class="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-300 forced-colors:appearance-auto"
                  type="checkbox"
                  @click="(e) => {
                    makeLanguageDefault(lang.locale)
                    e.stopPropagation()
                  }"
                />
              </div>
              <div class="group grid size-4 grid-cols-1">
                <font-awesome-icon
                  v-if="lang.isCreated"
                  :icon="['fas', 'trash']"
                  :class="{
                    'text-indigo-600 h-4 w-4 mx-1 ml-2': true,
                    'opacity-50': lang.isDefault
                  }"
                  @click="deleteLocalization(lang.locale)"
                />
                <font-awesome-icon
                  v-else
                  :icon="['fas', 'plus']"
                  class="text-indigo-600 h-4 w-4 mx-1 ml-2"
                  @click="addLocalization(lang.locale)"
                />
              </div>
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<style>
.lang-tab {
  @apply inline-block p-4 pb-0 border-b-2 border-transparent rounded-t-lg hover:border-purple-600;
}
</style>
